import React, { useState, useEffect } from "react";
import DonutChart from "./DonutChart";
import BubbleChart from "./BubbleChart";
import { getCbomCertificates, fetchCertificates } from "services/apiServices";
import { CbomCertificate } from "@schemas";
import { useProjectContext } from "context/ProjectContext";

const CertificatesSummary: React.FC = () => {
  const { state } = useProjectContext();
  const [allCertificates, setAllCertificates] = useState<CbomCertificate[]>([]);
  const [certificateData, setCertificateData] = useState([
    {
      description: "Total No. of Certificates",
      value: 0,
    },
    {
      description: "No. of Certificates Vulnerable (Signature Algorithm (QV))",
      value: 0,
      percent: "",
    },
    {
      description: "No. of Certificates Vulnerable (Public Key Algorithm (QV))",
      value: 0,
      percent: "",
    },
    {
      description: "No. of Certificates Expired",
      value: 0,
      percent: "",
    },
    {
      description: "No. of Certificates Expiring within a 7 days",
      value: 0,
      percent: "",
    },
    {
      description: "No. of Certificates Revoked (CRL Status)",
      value: 0,
      percent: "",
    },
    {
      description: "No. of Certificates Not Revoked (CRL Status)",
      value: 0,
      percent: "",
    },
    {
      description: "No. of Certificates Unknown (CRL Status)",
      value: 0,
      percent: "",
    },
    {
      description: "No. of Certificates Not Valid, Revoked (OCSP Status)",
      value: 0,
      percent: "",
    },
    {
      description: "No. of Certificates Valid, Not Revoked (OCSP Status)",
      value: 0,
      percent: "",
    },
    {
      description: "No. of Certificates Unknown (OCSP Status)",
      value: 0,
      percent: "",
    },
  ]);
  const [originalSignatureAlgorithmData, setOriginalSignatureAlgorithmData] =
    useState<any[]>([]);
  const [publicKeyAlgorithmData, setPublicKeyAlgorithmData] = useState<any[]>(
    []
  );
  const [validityStatusData, setValidityStatusData] = useState([
    {
      name: "Valid",
      noOfItems: 0,
      color: "custom.norisk",
      value: "",
    },
    {
      name: "Expired",
      noOfItems: 0,
      color: "custom.riskveryhigh",
      value: "",
    },
  ]);
  const [crlStatusData, setCrlStatusData] = useState([
    {
      name: "unknown",
      noOfItems: 0,
      color: "custom.riskverylow",
      value: "",
    },
    {
      name: "Revoked",
      noOfItems: 0,
      color: "custom.norisk",
      value: "",
    },
    {
      name: "Not Revoked",
      noOfItems: 0,
      color: "custom.riskveryhigh",
      value: "",
    },
  ]);
  const [ocspStatusData, setOcspStatusData] = useState([
    {
      name: "Unknown",
      noOfItems: 0,
      color: "custom.riskverylow",
      value: "",
    },
    {
      name: "Not Valid, Revoked",
      noOfItems: 0,
      color: "custom.norisk",
      value: "",
    },
    {
      name: "Valid, Not Revoked",
      noOfItems: 0,
      color: "custom.riskveryhigh",
      value: "",
    },
  ]);
  const [signatureAlgorithmQVData, setSignatureAlgorithmQVData] = useState([
    {
      name: "Unknown",
      noOfItems: 0,
      color: "custom.riskverylow",
      value: "",
    },
    { name: "Safe", noOfItems: 0, color: "custom.norisk", value: "" },
    {
      name: "Vulnerable",
      noOfItems: 0,
      color: "custom.riskveryhigh",
      value: "",
    },
  ]);
  const [publicKeyAlgorithmQVData, setPublicKeyAlgorithmQVData] = useState([
    {
      name: "Unknown",
      noOfItems: 0,
      color: "custom.riskverylow",
      value: "",
    },
    { name: "Safe", noOfItems: 0, color: "custom.norisk", value: "" },
    {
      name: "Vulnerable",
      noOfItems: 0,
      color: "custom.riskveryhigh",
      value: "",
    },
  ]);
  const [timeToExpiredData, setTimeToExpiredData] = useState([
    {
      name: "Not Expiring",
      noOfItems: 0,
      color: "custom.norisk",
      value: "",
    },
    {
      name: "Expiring within a 7 days",
      noOfItems: 0,
      color: "custom.riskverylow",
      value: "",
    },
    {
      name: "Expiring within a 30 days",
      noOfItems: 0,
      color: "custom.riskhigh",
      value: "",
    },
    {
      name: "Expired",
      noOfItems: 0,
      color: "custom.riskveryhigh",
      value: "",
    },
  ]);

  const formatPercent = (partial: number, total: number) =>
    total > 0 ? `(${((partial / total) * 100).toFixed(0)}%)` : "";
  // Random RGBA color generator
  const randomRGBA = () =>
    `rgba(${Math.floor(Math.random() * 256)}, ${Math.floor(
      Math.random() * 256
    )}, ${Math.floor(Math.random() * 256)}, 0.4)`;

  const calculateBubbleData = (counts: Record<string, number>) =>
    Object.entries(counts).map(([name, count], index) => ({
      id: index + 1,
      name: `${name}: ${count}`,
      fillColor: randomRGBA(),
      size: 30 + count * 4,
      dYdX1: {
        dy: Math.floor(Math.random() * 7) - 3,
        dx: Math.floor(Math.random() * 7) - 3,
      },
    }));

  const processCbomCertificates = async () => {
    try {
      // const response = await getCbomCertificates();
      // const certificates = response.data;

      const validityMap = { valid: 0, expired: 0 };
      const originalSignatureAlgoCounts: Record<string, number> = {};
      const publicKeyCounts: Record<string, number> = {};
      const crlStatusMap: Record<string, number> = {
        "unknown": 0,
        revoked: 0,
        "not revoked": 0,
      };
      const ocspStatusMap: Record<string, number> = {
        unknown: 0,
        "not valid, revoked": 0,
        "valid, not revoked": 0,
      };
      const signatureAlgoQVMap: Record<string, number> = {
        Unknown: 0,
        Safe: 0,
        Vulnerable: 0,
      };
      const publicKeyAlgoQVMap: Record<string, number> = {
        Unknown: 0,
        Safe: 0,
        Vulnerable: 0,
      };
      const timeToExpireMap: Record<string, number> = {
        notExpiring: 0,
        expiring7: 0,
        expiring30: 0,
        expired: 0,
      };

      allCertificates.forEach(
        ({
          validity_status,
          original_signature_algorithm,
          public_key_algorithm,
          crl_status,
          ocsp_status,
          signature_algorithm_qv,
          public_key_algorithm_qv,
          time_of_expiry_days,
          expiry_date,
        }: CbomCertificate) => {
          if (validity_status)
            validityMap[
              validity_status.toLowerCase() as keyof typeof validityMap
            ]++;

          if (original_signature_algorithm)
            originalSignatureAlgoCounts[original_signature_algorithm] =
              (originalSignatureAlgoCounts[original_signature_algorithm] || 0) +
              1;

          if (public_key_algorithm)
            publicKeyCounts[public_key_algorithm] =
              (publicKeyCounts[public_key_algorithm] || 0) + 1;

          if (crl_status) {
            const status = crl_status.toLowerCase();
	    console.log(status, crlStatusMap[status]);
            crlStatusMap[status] = (crlStatusMap[status] || 0) + 1;
          }

          if (ocsp_status) {
            const status = ocsp_status.toLowerCase();
            ocspStatusMap[status] = (ocspStatusMap[status] || 0) + 1;
          }

          if (signature_algorithm_qv)
            signatureAlgoQVMap[signature_algorithm_qv] =
              (signatureAlgoQVMap[signature_algorithm_qv] || 0) + 1;

          if (public_key_algorithm_qv)
            publicKeyAlgoQVMap[public_key_algorithm_qv] =
              (publicKeyAlgoQVMap[public_key_algorithm_qv] || 0) + 1;

          // if (time_of_expiry_days) {
          //   if (time_of_expiry_days < 0) {
          //     timeToExpireMap.expired++;
          //   } else if (time_of_expiry_days <= 7) {
          //     timeToExpireMap.expiring7++;
          //   } else if (time_of_expiry_days <= 30) {
          //     timeToExpireMap.expiring30++;
          //   } else {
          //     timeToExpireMap.notExpiring++;
          //   }
          // }
          if (expiry_date) {
            const expiry = new Date(expiry_date);
            const now = new Date();
            const diffMs = expiry.getTime() - now.getTime();
            const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

            if (diffDays < 0) {
              timeToExpireMap.expired++;
            } else if (diffDays <= 7) {
              timeToExpireMap.expiring7++;
            } else if (diffDays <= 30) {
              timeToExpireMap.expiring30++;
            } else {
              timeToExpireMap.notExpiring++;
            }
          }
        }
      );

      console.log(ocspStatusMap);

      setCertificateData([
        {
          description: "Total No. of Certificates",
          value: allCertificates.length,
        },
        {
          description:
            "No. of Certificates Vulnerable (Signature Algorithm (QV))",
          value: signatureAlgoQVMap.Vulnerable,
          percent: formatPercent(
            signatureAlgoQVMap.Vulnerable,
            allCertificates.length
          ),
        },
        {
          description:
            "No. of Certificates Vulnerable (Public Key Algorithm (QV))",
          value: publicKeyAlgoQVMap.Vulnerable,
          percent: formatPercent(
            publicKeyAlgoQVMap.Vulnerable,
            allCertificates.length
          ),
        },
        {
          description: "No. of Certificates Expired",
          value: validityMap.expired,
          percent: formatPercent(validityMap.expired, allCertificates.length),
        },
        {
          description: "No. of Certificates Expiring within a 7 days",
          value: timeToExpireMap.expiring7,
          percent: formatPercent(
            timeToExpireMap.expiring7,
            allCertificates.length
          ),
        },
        {
          description: "No. of Certificates Revoked (CRL Status)",
          value: crlStatusMap.revoked,
          percent: formatPercent(crlStatusMap.revoked, allCertificates.length),
        },
        {
          description: "No. of Certificates Not Revoked (CRL Status)",
          value: crlStatusMap["not revoked"],
          percent: formatPercent(
            crlStatusMap["not revoked"],
            allCertificates.length
          ),
        },
        {
          description: "No. of Certificates Unknown (CRL Status)",
          value: crlStatusMap["unknown"],
          percent: formatPercent(crlStatusMap["unknown"], allCertificates.length),
        },
        {
          description: "No. of Certificates Not Valid, Revoked (OCSP Status)",
          value: ocspStatusMap["not valid, revoked"],
          percent: formatPercent(
            ocspStatusMap["not valid, revoked"],
            allCertificates.length
          ),
        },
        {
          description: "No. of Certificates Valid, Not Revoked (OCSP Status)",
          value: ocspStatusMap["valid, not revoked"],
          percent: formatPercent(
            ocspStatusMap["valid, not revoked"],
            allCertificates.length
          ),
        },
        {
          description: "No. of Certificates Unknown (OCSP Status)",
          value: ocspStatusMap.unknown,
          percent: formatPercent(ocspStatusMap.unknown, allCertificates.length),
        },
      ]);
      setOriginalSignatureAlgorithmData(
        calculateBubbleData(originalSignatureAlgoCounts)
      );
      setPublicKeyAlgorithmData(calculateBubbleData(publicKeyCounts));
      setValidityStatusData([
        {
          name: "Valid",
          noOfItems: validityMap.valid,
          color: "custom.norisk",
          value: "",
        },
        {
          name: "Expired",
          noOfItems: validityMap.expired,
          color: "custom.riskveryhigh",
          value: "",
        },
      ]);
      setCrlStatusData([
        {
          name: "unknown",
          noOfItems: crlStatusMap["unknown"],
          color: "custom.riskverylow",
          value: "",
        },
        {
          name: "Revoked",
          noOfItems: crlStatusMap.revoked,
          color: "custom.norisk",
          value: "",
        },
        {
          name: "Not Revoked",
          noOfItems: crlStatusMap["not revoked"],
          color: "custom.riskveryhigh",
          value: "",
        },
      ]);
      setOcspStatusData([
        {
          name: "Unknown",
          noOfItems: ocspStatusMap.unknown,
          color: "custom.riskverylow",
          value: "",
        },
        {
          name: "Not Valid, Revoked",
          noOfItems: ocspStatusMap["not valid, revoked"],
          color: "custom.norisk",
          value: "",
        },
        {
          name: "Valid, Not Revoked",
          noOfItems: ocspStatusMap["valid, not revoked"],
          color: "custom.riskveryhigh",
          value: "",
        },
      ]);
      setSignatureAlgorithmQVData([
        {
          name: "Unknown",
          noOfItems: signatureAlgoQVMap.Unknown,
          color: "custom.riskverylow",
          value: "",
        },
        {
          name: "Safe",
          noOfItems: signatureAlgoQVMap.Safe,
          color: "custom.norisk",
          value: "",
        },
        {
          name: "Vulnerable",
          noOfItems: signatureAlgoQVMap.Vulnerable,
          color: "custom.riskveryhigh",
          value: "",
        },
      ]);
      setPublicKeyAlgorithmQVData([
        {
          name: "Unknown",
          noOfItems: publicKeyAlgoQVMap.Unknown,
          color: "custom.riskverylow",
          value: "",
        },
        {
          name: "Safe",
          noOfItems: publicKeyAlgoQVMap.Safe,
          color: "custom.norisk",
          value: "",
        },
        {
          name: "Vulnerable",
          noOfItems: publicKeyAlgoQVMap.Vulnerable,
          color: "custom.riskveryhigh",
          value: "",
        },
      ]);
      setTimeToExpiredData([
        {
          name: "Not Expiring",
          noOfItems: timeToExpireMap.notExpiring,
          color: "custom.norisk",
          value: "",
        },
        {
          name: "Expiring within a 7 days",
          noOfItems: timeToExpireMap.expiring7,
          color: "custom.riskverylow",
          value: "",
        },
        {
          name: "Expiring within a 30 days",
          noOfItems: timeToExpireMap.expiring30,
          color: "custom.riskhigh",
          value: "",
        },
        {
          name: "Expired",
          noOfItems: timeToExpireMap.expired,
          color: "custom.riskveryhigh",
          value: "",
        },
      ]);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const fetchCbomCertificates = () => {
      if (state.currentProjectID) {
        fetchCertificates(
          (data) => {
            setAllCertificates(data);
          },
          "",
          false,
          state.currentProjectID
        );
      }
    };
    fetchCbomCertificates();
  }, []);

  useEffect(() => {
    processCbomCertificates();
  }, [allCertificates]);

  return (
    <div className="bg-custom-blue8 rounded-lg px-[10px] py-[20px]">
      <div className="bg-white py-5 rounded-lg shadow-md space-y-5 mb-4">
        {certificateData.map((item, index) => (
          <div key={index} className="flex justify-between items-center px-14">
            <p className="w-1/2 text-custom-blue6 text-sm font-normal">
              {item.description}
            </p>
            <p className="w-1/4 text-custom-blue6 text-sm font-semibold">
              {item.value + ` ${item.percent ? item.percent : ""}`}
            </p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fit,minmax(400px,1fr))] gap-4 w-full">
        <div>
          {originalSignatureAlgorithmData.length > 0 && (
            <BubbleChart
              title="Original Signature Algorithm"
              bubbleData={originalSignatureAlgorithmData}
            />
          )}
        </div>
        <div>
          <DonutChart
            absoluteYearsData={signatureAlgorithmQVData}
            title="Quantum Vulnerability (Signature Algorithm)"
            footer=""
          />
        </div>
        <div>
          {publicKeyAlgorithmData.length > 0 && (
            <BubbleChart
              title="Public Key Algorithm"
              bubbleData={publicKeyAlgorithmData}
            />
          )}
        </div>
        <div>
          <DonutChart
            absoluteYearsData={publicKeyAlgorithmQVData}
            title="Quantum Vulnerability (Public Key Algorithm)"
            footer=""
          />
        </div>
        <div>
          <DonutChart
            absoluteYearsData={timeToExpiredData}
            title="Time to Expired Chart"
            footer=""
          />
        </div>
        <div className="mx-auto p-[10px] bg-white rounded-md shadow-md w-full flex items-center">
          <div className="w-full overflow-auto custom-scrollbar rounded-lg border max-h-[358px]">
            <table
              id="data-table"
              className="min-w-full bg-white shadow rounded-lg"
            >
              <thead className="sticky top-0 z-10 bg-custom-blue0 ">
                <tr className="bg-custom-blue0 text-sm text-left text-custom-blue7">
                  <th className="p-2">S. No</th>
                  <th className="p-2">Time to Expire Status</th>
                  <th className="p-2">No. Of Certificates</th>
                </tr>
              </thead>
              <tbody>
                {timeToExpiredData.map((data, index) => (
                  <tr
                    key={index}
                    className={`${
                      index % 2 === 1 ? "bg-custom-blue0" : ""
                    } hover:bg-custom-blue0 text-sm text-custom-blue7`}
                  >
                    <td className="p-2">{index + 1}</td>
                    <td className="p-2">{data.name}</td>
                    <td className="p-2">{data.noOfItems}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <DonutChart
            absoluteYearsData={crlStatusData}
            title="CRL Status"
            footer=""
          />
        </div>
        <div>
          <DonutChart
            absoluteYearsData={ocspStatusData}
            title="OCSP Status"
            footer=""
          />
        </div>
        <div>
          <DonutChart
            absoluteYearsData={validityStatusData}
            title="Validity Status"
            footer=""
          />
        </div>
      </div>
    </div>
  );
};

export default CertificatesSummary;
